import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import azureDevOpsPlugin from '@backstage-community/plugin-azure-devops/alpha';
import sonarqubePlugin from '@backstage-community/plugin-sonarqube/alpha';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import { navModule } from './modules/nav';
import { redirectModule } from './modules/redirect';
import { teamLandingModule } from './modules/team-landing';
import { jiraCardModule } from './modules/jira';
import { confluenceCardModule } from './modules/confluence-search';
import { tenableCardModule } from './modules/tenable-card';
import { adoArtifactsCardModule } from './modules/ado-artifacts-card';
import { bitbucketCardModule } from './modules/bitbucket-card';
import { acrCardModule } from './modules/acr-card';
import { growthbookCardModule } from './modules/growthbook-card';
import { figmaCardModule } from './modules/figma-card';
import { storybookCardModule } from './modules/storybook-card';
import { rovoCardModule } from './modules/rovo-card';
import { leanixCardModule } from './modules/leanix-card';
import { environmentUrlsCardModule } from './modules/environment-urls';
import { performanceDashboardCardModule } from './modules/performance-dashboard';
import { serviceConnectionAccountCardModule } from './modules/service-connection-account';
import { appInsightsCardModule } from './modules/app-insights-card';
import { deployedVersionsTabModule } from './modules/kubernetes-versions';
import { npmAuditCardModule } from './modules/npm-audit-card';
import { releasesPageModule, releasesEntityCardModule } from './modules/releases';
import { maintenancePageModule } from './modules/maintenance';

import { convertLegacyAppOptions } from '@backstage/core-compat-api';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { SignInPage } from '@backstage/core-components';

const authModule = convertLegacyAppOptions({
  components: {
    SignInPage: (props: any) => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'microsoft-auth-provider',
          title: 'Sign in',
          message: 'Please use your Luminus account',
          apiRef: microsoftAuthApiRef,
        }}
      />
    ),
  },
});

export default createApp({
  features: [
    catalogPlugin,
    // DPT-492 (T083) — Catalog Graph: renders a "Relations" card on every
    // entity overview and a standalone `/catalog-graph` explorer (auto-listed
    // in the sidebar via `nav.rest()`). Works off relations already in the
    // catalog (ownedBy / dependsOn / partOf …) — no per-component annotation
    // and no backfill dependency, so it sits outside the Phase 6 tool-card set.
    catalogGraphPlugin,
    orgPlugin,
    azureDevOpsPlugin,
    sonarqubePlugin,
    kubernetesPlugin,
    techdocsPlugin,
    // Provides the searchApiRef implementation (plugin.search.queryservice)
    // that SidebarSearchModal in the nav module depends on. Without it the
    // search modal throws NotImplementedError on render.
    searchPlugin,
    navModule,
    authModule,
    redirectModule,
    teamLandingModule,
    // US4 (DPT-491) — service-to-tool tiles. Each module registers an
    // EntityCardBlueprint on the Component overview; cards render `null`
    // when their annotation is missing and are wrapped in TileBoundary so
    // a broken integration degrades that tile only (spec FR-008).
    bitbucketCardModule,
    adoArtifactsCardModule,
    acrCardModule,
    tenableCardModule,
    growthbookCardModule,
    figmaCardModule,
    storybookCardModule,
    jiraCardModule,
    confluenceCardModule,
    rovoCardModule,
    leanixCardModule,
    // US9 (DPT-505) — per-environment deployed-app URLs (DEV/TST/ACC/PRD +
    // optional Swagger) from the `luminus.be/` annotation prefix. Link-out
    // only (opens each URL in a new tab); renders `null` when unannotated.
    environmentUrlsCardModule,
    // US10 (DPT-506) — one-click link to the team's Azure performance
    // dashboard from `luminus.be/performance-dashboard-url`. Link-out only;
    // renders `null` when the annotation is absent.
    performanceDashboardCardModule,
    // Service connection account (spec 002 / DPT-511) — read-only tile showing
    // the `dev.azure.com/service-connection-account` value: the service account
    // that links this component's ADO pipeline to its Bitbucket repo. Plain
    // text, no link-out; renders `null` when the annotation is absent/blank.
    serviceConnectionAccountCardModule,
    // US15 (Phase 15 / DPT-492 spillover) — per-environment Azure Application
    // Insights correlation from the `azure.com/app-insights-` annotation family.
    // A `resource-id-<env>` (full ARM path) drives a real Azure portal deep
    // link; components carrying only the older `application-id-<env>` GUID get a
    // read-only fallback line. Renders `null` when unannotated (spec FR-008).
    appInsightsCardModule,
    // US11 (DPT-503) — npm dependency vulnerability counts (Critical / High /
    // Moderate / Low) from the `npm-audit` build artefact published by the PR
    // pipeline. Fetches via the `/azure-devops` proxy off the existing
    // `dev.azure.com/build-definition` annotation; renders `null` when absent
    // and degrades per-tile via TileBoundary (spec FR-008).
    npmAuditCardModule,
    // US12 (DPT-508) — per-cluster deployed-version summary read from the
    // Kubernetes plugin's useKubernetesObjects() hook (no proxy/backend fetch).
    // Reuses the existing `backstage.io/kubernetes-label-selector` annotation;
    // renders `null` when it is absent and wraps the tile in TileBoundary.
    // Surfaced on its own "Deployed versions" entity tab, adjacent to the stock
    // Kubernetes tab (the plugin's routable content can't be wrapped in-tab).
    deployedVersionsTabModule,
    // Global Release Feed (spec 004-release-feed) — GrowthBook-gated
    // (`backstage-releases-view`) /releases area: feed, per-component history,
    // and CHANGELOG coverage. Flag-aware sidebar entry in modules/nav/Sidebar.tsx.
    releasesPageModule,
    // Its flag-gated entity-page card (DPT-594) linking a Component to its
    // release history — registered under the `catalog` plugin.
    releasesEntityCardModule,
    // Maintenance Windows (spec 003) — GrowthBook-gated (`backstage-maintenance-view`)
    // area for maintenance-mode-service schedules + the application registry.
    // Flag-aware nav entry registered in modules/nav/Sidebar.tsx.
    maintenancePageModule,
  ],
});
